import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Spinner, useAddToast } from '@hm-group/bads-react';
import { Home } from '../Home/Home';
import { msalScopes } from '../../services/msalConfig';
import {
  getLastLoginTimestamp,
  setLastLoginTimestamp,
  isSessionExpired,
  forceRelogin,
} from '../../utils/sessionExpiry';
import styles from './App.module.css';

function isRedirectCallbackUrl(): boolean {
  const url = globalThis.location.href;
  return url.includes('code=') || url.includes('error=') || url.includes('state=');
}

export function App() {
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const { addToast } = useAddToast();

  // Initialize loading to false if we're already on a redirect callback URL
  const [isLoading, setIsLoading] = useState(() => !isRedirectCallbackUrl());

  // Handle authentication - run once on mount
  useEffect(() => {
    if (isRedirectCallbackUrl()) {
      return;
    }

    const initAuth = async () => {
      try {
        await instance.initialize();
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          if (isSessionExpired()) {
            forceRelogin(instance, addToast);
            return;
          }
          if (getLastLoginTimestamp() === null) {
            // Feature just shipped for an already-active session - start the clock now.
            setLastLoginTimestamp();
          }
          return;
        }
        if (inProgress === InteractionStatus.None) {
          instance.loginRedirect({
            scopes: msalScopes.loginScopes,
            prompt: 'select_account',
          });
        }
      } catch (err) {
        console.error('MSAL init error:', err);
      }
    };

    setIsLoading(false);
    initAuth();
  }, [instance, inProgress, addToast]);

  // Navigate after auth interaction completes
  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        // Navigate after auth if on redirect URL
        const currentUrl = globalThis.location.href;
        if (
          currentUrl.includes('code=') ||
          currentUrl.includes('error=') ||
          currentUrl.includes('state=')
        ) {
          // A fresh interactive login just completed - reset the 24h clock here only.
          setLastLoginTimestamp();
          const cleanUrl = globalThis.location.origin + globalThis.location.pathname;
          globalThis.history.replaceState({}, document.title, cleanUrl);
          navigate('/', { replace: true });
        }
      }
    }
  }, [inProgress, instance, navigate]);

  // Re-check expiry when the tab regains focus (catches a tab left open across the 24h boundary)
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      if (instance.getAllAccounts().length === 0) return;
      if (isSessionExpired()) {
        forceRelogin(instance, addToast);
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [instance, addToast]);

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <p>Checking system status...</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
